import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schema,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title.includes('被窝影院') ? title : `${title} - 被窝影院`;

    document.title = fullTitle;

    const setMeta = (name: string, content: string, property?: string) => {
      let el: HTMLMetaElement | null;
      if (property) {
        el = document.querySelector(`meta[property="${property}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', property);
          document.head.appendChild(el);
        }
      } else {
        el = document.querySelector(`meta[name="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('name', name);
          document.head.appendChild(el);
        }
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    if (canonicalUrl) {
      let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    setMeta('og:title', fullTitle, 'og:title');
    setMeta('og:description', description, 'og:description');
    setMeta('og:type', ogType, 'og:type');
    if (canonicalUrl) setMeta('og:url', canonicalUrl, 'og:url');
    if (ogImage) setMeta('og:image', ogImage, 'og:image');
    setMeta('og:site_name', '被窝影院', 'og:site_name');
    setMeta('og:locale', 'zh_CN', 'og:locale');

    setMeta('twitter:card', 'summary_large_image', 'twitter:card');
    setMeta('twitter:title', fullTitle, 'twitter:title');
    setMeta('twitter:description', description, 'twitter:description');
    if (ogImage) setMeta('twitter:image', ogImage, 'twitter:image');

    if (noindex) {
      let robots: HTMLMetaElement | null = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, follow');
    }

    if (schema) {
      let script: HTMLScriptElement | null = document.querySelector('script[type="application/ld+json"][data-seo]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-seo', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    }

    return () => {
      const seoScript = document.querySelector('script[type="application/ld+json"][data-seo]');
      if (seoScript) seoScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, schema, noindex]);

  return null;
}